import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FieldError } from '@/components/ui/field-error';
import { WHITELISTED_PAYOUT_ADDRESSES_QUERY_KEY } from '@/hooks/queries/compliance/use-whitelisted-payout-addresses';
import { getErrorMessage } from '@/lib/api/error';
import {
  type AddressFormValues,
  buildCreatePayload,
  buildUpdatePayload,
  createWhitelistedPayoutAddress,
  updateWhitelistedPayoutAddress,
  WHITELISTED_ADDRESS_TYPES,
  type WhitelistedPayoutAddress,
  type WhitelistedPayoutAddressType,
} from '@/services/api/whitelisted-payout-addresses';

const COMMENT_MIN_LENGTH = 10;
const COMMENT_ROWS = 3;

// Legacy quirk kept deliberately: this form enforces ≥10 comment chars
// while the delete flow's shared audit dialog enforces 35–500 — do not normalise.
const addressFormSchema = z
  .object({
    name: z.string().min(1, 'This field is required'),
    type: z.enum(WHITELISTED_ADDRESS_TYPES),
    currency: z.string().min(1, 'This field is required'),
    iban: z.string(),
    wallet_address: z.string(),
    comment: z.string().min(1, 'This field is required').min(COMMENT_MIN_LENGTH, 'Comment must be at least 10 characters'),
  })
  // requiredIban/requiredWallet depend on `type`, not the field value alone —
  // expressed as whole-form refinements so they re-run whenever `type` changes.
  .superRefine((values, context) => {
    if (values.type === 'IBAN' && !values.iban) {
      context.addIssue({ code: 'custom', path: ['iban'], message: 'IBAN is required when type is IBAN' });
    }
    if (values.type === 'Blockchain' && !values.wallet_address) {
      context.addIssue({ code: 'custom', path: ['wallet_address'], message: 'Wallet address is required when type is Blockchain' });
    }
  });

const CREATE_DEFAULTS: AddressFormValues = { name: '', currency: '', type: 'IBAN', iban: '', wallet_address: '', comment: '' };

// Comment always starts empty — a fresh justification is required per edit
const toEditDefaults = (address: WhitelistedPayoutAddress): AddressFormValues => ({
  name: address.name,
  currency: address.currency,
  type: address.type,
  iban: address.iban || '',
  wallet_address: address.wallet_address || '',
  comment: '',
});

interface AddressFormDialogProps {
  /** Row being edited; null = create mode */
  address: WhitelistedPayoutAddress | null;
  onClose: () => void;
}

// Mounted per open — unmounting resets the form (legacy close+reset semantics)
export const AddressFormDialog = ({ address, onClose }: AddressFormDialogProps) => {
  const queryClient = useQueryClient();
  const isEditMode = address !== null;

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: isEditMode ? toEditDefaults(address) : CREATE_DEFAULTS,
    mode: 'onChange', // submit stays disabled until the form is valid (legacy parity)
  });
  const { errors, isSubmitting, isValid } = form.formState;
  const type = form.watch('type');
  const comment = form.watch('comment');

  // Switching type clears the opposite identifier so a stale value is never submitted
  // and its conditional validation error is dropped (legacy onTypeChange)
  const handleTypeChange = (nextType: WhitelistedPayoutAddressType) => {
    form.setValue('type', nextType, { shouldValidate: true, shouldDirty: true });
    if (nextType === 'IBAN') form.setValue('wallet_address', '', { shouldValidate: true });
    else form.setValue('iban', '', { shouldValidate: true });
  };

  const handleSubmit = form.handleSubmit(async (rawValues) => {
    // Deliberate fix: currency is free text feeding an asset-icon URL —
    // uppercase-trim so casing typos don't break the icon or duplicate entries.
    const values: AddressFormValues = { ...rawValues, currency: rawValues.currency.trim().toUpperCase() };
    try {
      if (isEditMode) {
        await updateWhitelistedPayoutAddress(buildUpdatePayload(address, values));
        toast.success('Whitelisted payout address updated successfully');
      } else {
        await createWhitelistedPayoutAddress(buildCreatePayload(values));
        toast.success('Whitelisted payout address created successfully');
      }
      await queryClient.invalidateQueries({ queryKey: WHITELISTED_PAYOUT_ADDRESSES_QUERY_KEY });
      onClose();
    } catch (error) {
      // Upstream validation messages (e.g. "Comment is required") surface here; the
      // dialog stays open so the operator can correct and resubmit.
      toast.error(getErrorMessage(error));
    }
  });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      {/* persistent (legacy): backdrop clicks and Esc must not discard a half-filled form */}
      <DialogContent className="sm:max-w-[600px]" onEscapeKeyDown={(event) => event.preventDefault()} onInteractOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? <Pencil className="size-5" /> : <Plus className="size-5" />}
            {isEditMode ? 'Edit Whitelisted Payout Address' : 'Create New Whitelisted Payout Address'}
          </DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="address-name">Name *</Label>
            <Input id="address-name" {...form.register('name')} />
            <FieldError message={errors.name?.message} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="address-type">Type *</Label>
            <Select onValueChange={(value) => handleTypeChange(value as WhitelistedPayoutAddressType)} value={type}>
              <SelectTrigger id="address-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WHITELISTED_ADDRESS_TYPES.map((addressType) => (
                  <SelectItem key={addressType} value={addressType}>
                    {addressType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError message={errors.type?.message} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="address-currency">Currency *</Label>
            <Input id="address-currency" {...form.register('currency')} />
            <FieldError message={errors.currency?.message} />
          </div>

          {type === 'IBAN' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-iban">IBAN *</Label>
              <Input id="address-iban" {...form.register('iban')} />
              <FieldError message={errors.iban?.message} />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-wallet">Wallet Address *</Label>
              <Input id="address-wallet" {...form.register('wallet_address')} />
              <FieldError message={errors.wallet_address?.message} />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="address-comment">Comment (for audit trail) *</Label>
            <Textarea
              id="address-comment"
              placeholder={isEditMode ? 'Explain what changed and why (min. 10 characters)' : 'Explain the reason for creating this address (min. 10 characters)'}
              rows={COMMENT_ROWS}
              {...form.register('comment')}
            />
            <p className="text-xs text-muted-foreground">
              {comment.length} characters (minimum {COMMENT_MIN_LENGTH})
            </p>
            <FieldError message={errors.comment?.message} />
          </div>

          <div className="flex justify-end gap-2">
            <Button disabled={isSubmitting} onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button className="gap-2" disabled={!isValid || isSubmitting} type="submit" variant="primary">
              {isSubmitting ? <Spinner className="size-4" /> : null}
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
AddressFormDialog.displayName = 'AddressFormDialog';
